/**
 * Sticky 50/50 A/B assignment. One test runs at a time (see ab_tests table).
 * The variant is kept in localStorage so a visitor always sees the same version.
 */
export const ACTIVE_TEST_ID = "quote_cta_v1";

export const QUOTE_CTA_COPY = {
  A: "Request a Quote",
  B: "Get My Factory Price",
} as const;

export type Variant = "A" | "B";

const KEY = `jttg_ab_${ACTIVE_TEST_ID}`;

export const getVariant = (): Variant => {
  if (typeof window === "undefined") return "A";
  try {
    const forced = new URLSearchParams(window.location.search).get("ab");
    if (forced === "A" || forced === "B") {
      localStorage.setItem(KEY, forced);
      return forced;
    }
    const saved = localStorage.getItem(KEY);
    if (saved === "A" || saved === "B") return saved;
    const v: Variant = Math.random() < 0.5 ? "A" : "B";
    localStorage.setItem(KEY, v);
    return v;
  } catch {
    return "A";
  }
};

/** Button wording for the main quote CTA under the running test. */
export const quoteCtaLabel = (): string => QUOTE_CTA_COPY[getVariant()];

/**
 * Two-proportion z-test. Returns null until each version has at least
 * `minStarts` quote starts, so small samples never declare a winner.
 */
export const evaluateTest = (
  a: { starts: number; completions: number },
  b: { starts: number; completions: number },
  minStarts = 100,
): { winner: Variant | null; confident: boolean; z: number } | null => {
  if (a.starts < minStarts || b.starts < minStarts) return null;
  const pa = a.completions / a.starts;
  const pb = b.completions / b.starts;
  const p = (a.completions + b.completions) / (a.starts + b.starts);
  const se = Math.sqrt(p * (1 - p) * (1 / a.starts + 1 / b.starts));
  const z = se ? (pb - pa) / se : 0;
  const confident = Math.abs(z) >= 1.96;
  return { winner: confident ? (z > 0 ? "B" : "A") : null, confident, z };
};
